import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { formatCurrency } from '../utils/currency';
import { LoadingState } from '../components/Loading';
import { FaArrowLeft } from 'react-icons/fa';

const STATUS_BADGE = {
  pending: 'warning',
  processing: 'info',
  shipped: 'info',
  delivered: 'success',
  cancelled: 'danger',
};

const OrderDetail = () => {
  const { id } = useParams();
  const { settings } = useSettings();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadOrder(); }, [id]);

  const loadOrder = async () => {
    try {
      const res = await axios.get(`/api/orders/${id}`);
      setOrder(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="page"><LoadingState /></div>;
  if (!order) return (
    <div className="page">
      <div className="container empty-state">
        <h3>Order not found</h3>
        <Link to="/my-orders" className="btn btn-primary">My orders</Link>
      </div>
    </div>
  );

  const addr = order.shippingAddress || {};

  return (
    <div className="page">
      <div className="container">
        <Link to="/my-orders" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--gray-600)', fontSize: 14, marginBottom: 24 }}>
          <FaArrowLeft /> Back to orders
        </Link>

        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 30, marginBottom: 4 }}>
              Order {order.orderNumber}
            </h1>
            <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>
              Placed on {new Date(order.createdAt).toLocaleDateString('en-US', { dateStyle: 'long' })}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span className={`badge ${STATUS_BADGE[order.status] || 'neutral'}`}>{order.status}</span>
            <span className={`badge ${order.paymentStatus === 'paid' ? 'success' : 'warning'}`}>
              {order.paymentStatus}
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 24, alignItems: 'start' }} className="order-detail-grid">
          {/* Items */}
          <div className="card">
            <div className="card-header">
              <h2>Items</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {order.items.map((item) => (
                <div key={item._id} style={{ display: 'flex', gap: 16, alignItems: 'center', paddingBottom: 16, borderBottom: '1px solid var(--gray-100)' }}>
                  <div style={{ width: 64, height: 64, borderRadius: 'var(--radius)', overflow: 'hidden', background: 'var(--gray-100)', flexShrink: 0 }}>
                    {item.product?.images?.[0] ? (
                      <img src={item.product.images[0].url} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : null}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{item.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
                      Quantity: {item.quantity} × {formatCurrency(item.price, settings?.currency)}
                    </div>
                  </div>
                  <div style={{ fontWeight: 700 }}>
                    {formatCurrency(item.subtotal, settings?.currency)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card">
              <div className="card-header">
                <h2>Order summary</h2>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}>
                <span>Subtotal</span>
                <span>{formatCurrency(order.subtotal, settings?.currency)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}>
                <span>Tax</span>
                <span>{formatCurrency(order.tax || 0, settings?.currency)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}>
                <span>Shipping</span>
                <span>{formatCurrency(order.shipping || 0, settings?.currency)}</span>
              </div>
              <div style={{ height: 1, background: 'var(--gray-100)', margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 17, fontWeight: 700 }}>
                <span>Total</span>
                <span>{formatCurrency(order.total, settings?.currency)}</span>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h2>Shipping address</h2>
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--gray-700)' }}>
                <strong>{addr.name}</strong><br />
                {addr.street}<br />
                {addr.city}, {addr.state} {addr.zipCode}<br />
                {addr.country}<br />
                {addr.phone && <>📞 {addr.phone}</>}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h2>Payment</h2>
              </div>
              <div style={{ fontSize: 14 }}>
                <strong style={{ textTransform: 'capitalize' }}>{order.paymentMethod}</strong>
                <div style={{ marginTop: 8, color: 'var(--gray-500)' }}>
                  Status: <span className={`badge ${order.paymentStatus === 'paid' ? 'success' : 'warning'}`}>{order.paymentStatus}</span>
                </div>
              </div>
            </div>

            {order.trackingNumber && (
              <div className="card">
                <div className="card-header">
                  <h2>Tracking</h2>
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 14 }}>{order.trackingNumber}</div>
              </div>
            )}
          </div>
        </div>

        <style>{`
          @media (max-width: 900px) {
            .order-detail-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default OrderDetail;
